import { get, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
/* A work order is the work-orders module's record wherever it appears, including
   embedded in `AnomalyOut`. Importing its mapper rather than writing a second one
   here is what stops the two disagreeing about `overdue`. */
import { orderFromWire as workOrderFromWire } from '@/features/workOrders/api'

/**
 * Anomalies — the real detection records.
 *
 * ── What the API actually carries, and nothing more ────────────────────────
 * This is the whole of a list row:
 *
 *   id, severity, status, serial_number, title, created_at, hierarchy{plant,line,machine}
 *
 * That is worth stating plainly because the design prototype for this screen
 * shows a great deal more — a reading against a threshold, a fusion score, an
 * assignee, a work order, a due date, per-sensor trend charts. None of it has a
 * field here. `features/alerts/api.js` describes those because it reads
 * FIXTURES; this reads the server. Nothing in this module may synthesise a
 * value the endpoint did not send.
 *
 * The detail read (`AnomalyOut`) adds five timestamps — `detected_at`,
 * `window_start`, `window_end`, `last_detected_at`, `updated_at` — and, since
 * the reshape, six fields that ARE new dimensions: `content`, `history`,
 * `sensor_tags`, `similar_anomalies`, `document_references` and `work_orders`.
 * The last three are real records, and they are what the Action tab used to
 * invent.
 *
 * ── Counts arrive with the list, not from a counts endpoint ────────────────
 * `GET /anomalies` embeds `counts.status` and `counts.severity`. Their scope is
 * NOT the scope of the rows beside them, and that asymmetry is deliberate
 * server-side: the counts respect the `created_from`/`created_to` range and the
 * `asset_type`/`asset_id` subtree, while search, status, severity and
 * plant/line/machine narrow the rows alone. So the facet tiles describe the
 * chosen period and place, and keep their numbers when you press one — which
 * is what makes them usable as filters.
 *
 * Search sat above the counts until backend dafc451 (2026-10-02) moved it below
 * them; the copy beside the tiles said otherwise for a week afterwards.
 */

/**
 * `sort_by` accepts exactly these three, and `sort_dir` exactly two.
 *
 * Mirrors `Literal["created_at", "severity", "status"]` and
 * `Literal["asc", "desc"]` in the router signature. Written out because they are
 * a closed contract the endpoint rejects deviation from with a 422 — the same
 * reason `VALUE_TYPES` is written out in the sensors module. Everything that
 * varies by DATA (which statuses exist, which severities, how many of each) is
 * read from the response instead; see `toCounts`.
 */
/**
 * `AnomalyStatus`, in the order the backend declares it — which is also the
 * lifecycle order, and what Postgres sorts an enum column by.
 *
 * Written out for the same reason `SORT_FIELDS` is: it is a request contract the
 * endpoint rejects deviation from, not data it reports.
 *
 * **This is the wire enum and it is deliberately still complete.** The product no
 * longer offers `resolved` (see `RETIRED_STATUSES`), but the server still declares
 * it, still returns it in `counts.status`, and still holds rows in it — so anything
 * that RENDERS a status must keep handling all four. Trimming this list would not
 * remove those rows; it would only make them render as an unlabelled hole.
 *
 * ── `escalated` was in here, and it is NOT a status ──────────────────────
 * It sat in this array for as long as `AnomalyStatus` had five members. The enum
 * has four. So sending it as a filter is a 422, `counts.status` can never report
 * it, and the facet tile built for it could never be anything but zero — a tile
 * for a queue that cannot exist. It is gone rather than retired: `RETIRED_STATUSES`
 * means "the product withdrew it", which is a different and recoverable thing.
 */
export const ANOMALY_STATUSES = ['open', 'acknowledged', 'resolved', 'dismissed']

/**
 * Why an anomaly was dismissed — `AnomalyStatusReason`, in the enum's order.
 *
 * Only `dismissed` takes one: `AnomalyUpdate` rejects a dismiss without a reason
 * and any other status WITH one. The labels live under
 * `anomalies.detail.dismissReasons`, keyed by these wire values.
 */
export const ANOMALY_STATUS_REASONS = ['false_positive', 'duplicate', 'known_condition', 'planned_work', 'other']

/**
 * The lifecycle the product actually offers, and the two states it withdrew.
 *
 * ── What changed, and what did not ────────────────────────────────────────
 * An anomaly is `open` on arrival and moves in exactly two ways: a person dismisses
 * it, or a person acknowledges it. `resolved` is not reachable from this UI — it was
 * another item in a menu with no owner, rule or consequence attached to it.
 *
 * ── Acknowledge is an explicit action now, and it had to become one ──────
 * It used to be chained off approving an analysis: approve the RCA, and the
 * anomaly moved to `acknowledged` behind it. There is no approval any more — the
 * analysis is the engine's and this client cannot write it — and that was the ONLY
 * path into `acknowledged`. Removing the approval would have silently removed the
 * status with it, so the detail screen offers it directly instead.
 *
 * `resolved` is **withdrawn from the interface, not deleted from the system**:
 * `PUT /anomalies/{id}` still accepts it, the enum still has it, and
 * `counts.status` still reports it. So the list hides its facet tile and still
 * renders a row that is in it, correctly labelled. A row the product can no longer
 * produce is still a row somebody has to be able to read.
 */
export const OFFERED_STATUSES = ['open', 'acknowledged', 'dismissed']
export const RETIRED_STATUSES = ['resolved']

/**
 * The statuses the facet row always shows, in lifecycle order.
 *
 * ── Why a list exists at all, when `counts.status` is read as data ────────
 * Because the server omits what it has none of. `counts.status` is a `GROUP BY`,
 * not a walk of the enum, so a status with no rows is not a zero — it is an absent
 * key, and a tile built only from what arrived disappears the moment the queue it
 * describes empties — so a shift that triages everything would be reported as a
 * screen with a tile missing rather than as a queue at zero. An empty queue is a
 * fact worth stating; a missing tile is not.
 *
 * `resolved` is not here on purpose — it is withdrawn from this interface, and
 * zero-filling it would put it back.
 *
 * This does NOT replace reading the response. `statusFacets` unions the two, so a
 * sixth status the backend adds tomorrow still appears on its own without a
 * release here — which was the whole reason the counts were read as data, and is
 * the property a hardcoded tile list would have thrown away.
 */
export const COUNTED_STATUSES = ['open', 'acknowledged', 'dismissed']

/**
 * `counts.status` → the tiles to draw.
 *
 * The known lifecycle first, zero-filled and in its own order, then anything else
 * the server reported that this client has never heard of. Nothing is dropped
 * except what the caller drops: `resolved` arrives here and the page removes it,
 * so the one place that decides what is withdrawn stays the one place.
 *
 * @param {Array<{key: string, count: number}>} counts as `toCounts` produces them
 */
export function statusFacets(counts = []) {
  const reported = new Map(counts.map((entry) => [entry.key, entry.count]))

  return [
    ...COUNTED_STATUSES.map((key) => ({ key, count: reported.get(key) ?? 0 })),
    ...counts.filter((entry) => !COUNTED_STATUSES.includes(entry.key)),
  ]
}

export const SORT_FIELDS = ['created_at', 'severity', 'status']
export const SORT_DIRECTIONS = ['asc', 'desc']

export const DEFAULT_SORT_FIELD = 'created_at'
export const DEFAULT_SORT_DIRECTION = 'desc'

/**
 * Product tier → `TargetLevel`, the vocabulary `asset_type` is parsed against.
 * Same split `features/hierarchy/parents.js` states for the id columns.
 */
const ASSET_TYPE_OF_TIER = { site: 'plant', line: 'line', asset: 'machine' }

/** `page_size: int = Query(default=50, ge=1, le=200)`. A 422 above the ceiling. */
export const DEFAULT_PAGE_SIZE = 50
export const MAX_PAGE_SIZE = 200

/**
 * Naive UTC → an instant the browser can localise.
 *
 * Every timestamp this API returns is `DateTime(timezone=False)` written by
 * `models/base.utcnow()`, so `"2026-08-11T10:21:06.649161"` is UTC with the
 * designator omitted. `new Date()` reads a bare string like that as LOCAL time,
 * which silently shifts every value by the reader's offset — five and a half
 * hours in IST, and in the wrong direction: a detection from this morning would
 * read as this afternoon, and `formatRelative` would call it "in 5 hours".
 *
 * So the designator is restored before parsing. Guarded rather than
 * unconditional, because the day the backend starts sending offsets this must
 * not append a second one and produce an Invalid Date.
 */
export function toInstant(value) {
  if (typeof value !== 'string' || value === '') return null
  // A trailing Z, or ±HH:MM after the time portion — not the date's own hyphens.
  return /(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? value : `${value}Z`
}

/**
 * Wire → app.
 *
 * `hierarchy` is carried through as the server's own shape rather than being
 * flattened here: the server resolves the names for whichever level the anomaly
 * attached to and leaves the deeper ones null, so `{plant, line, machine}` with
 * holes in it IS the record. Which level is populated is the fact the screen
 * renders — see `hierarchyTrail`.
 *
 * `severity` and `status` are passed through untranslated and unmapped. There is
 * no client-side vocabulary to convert them into: `common:status.acknowledged`
 * is keyed on the wire value, so a status the backend adds tomorrow reaches the
 * screen without a release here.
 */
const toAnomaly = (row) => ({
  id: row.id,
  severity: row.severity,
  status: row.status,
  serialNumber: row.serial_number,
  title: row.title,
  createdAt: toInstant(row.created_at),
  /* The attachment as IDS, not only as names. A work order linked to an anomaly
     must sit at that anomaly's own `(plant_id, line_id, machine_id)` — the
     server compares the triple exactly and 400s a mismatch — so anything
     raising work from an anomaly needs the ids, not the labels. */
  plantId: row.plant_id ?? null,
  lineId: row.line_id ?? null,
  machineId: row.machine_id ?? null,
  hierarchy: {
    plant: row.hierarchy?.plant ?? null,
    line: row.hierarchy?.line ?? null,
    machine: row.hierarchy?.machine ?? null,
  },
  /**
   * Whether an analysis exists. A BOOLEAN, where this used to read a five-value
   * `rca_status`.
   *
   * Server-derived rather than stored — `has_analysis=bool(anomaly.content)` —
   * so the list can say "investigated or not" without a request per row. What it
   * cannot say any more is HOW FAR an analysis has got: there is no queued, no
   * draft, no approved, because there is no RCA row to hold a status. There is a
   * `content` object or there is `{}`.
   *
   * ── No `??` fallback, deliberately ──────────────────────────────────────
   * `rcaStatus` had one (`?? 'pending'`), and when the server dropped the field
   * that fallback turned a missing value into a plausible wrong one on every
   * card in the estate — every anomaly read as "not investigated" while a full
   * report sat in the record, with nothing on screen looking broken. `=== true`
   * is deliberately strict so an absent field reads as absent rather than as a
   * confident `false`.
   */
  hasAnalysis: row.has_analysis === true,
})

/**
 * The detail read. The same record, five more timestamps, and six fields that are
 * genuinely new dimensions rather than more of the same.
 *
 * `has_analysis` IS on this shape now, so a detail screen can answer the question
 * from the record it just fetched. It could not before — the field was on the list
 * item alone — and the workaround was a second request whose 404 meant "nobody has
 * asked yet". That whole arrangement is gone; see the retired block below.
 */
export const anomalyDetailFromWire = (row) => ({
  ...toAnomaly(row),
  detectedAt: toInstant(row.detected_at),
  windowStart: toInstant(row.window_start),
  windowEnd: toInstant(row.window_end),
  lastDetectedAt: toInstant(row.last_detected_at),
  updatedAt: toInstant(row.updated_at),

  /**
   * The analysis itself — the column that replaced the RCA sub-resource.
   *
   * A bare JSONB column with no server-side schema, so nothing here may assume a
   * key. `{}` is the ordinary state of an anomaly nobody has investigated, and it
   * is the ONLY way "no analysis" is expressed now — there is no status to read.
   * See `toRcaSections` for how it is rendered without trusting its shape.
   */
  content: row.content ?? {},

  /**
   * The timeline of changes to `content`, oldest first.
   *
   * `{title, description, changed_at}` per entry, appended by `_append_history`
   * server-side whenever a copilot directive writes a field. `changed_at` is
   * naive UTC like every other timestamp in this API, so it goes through
   * `toInstant` — skipping that shifts each entry by the reader's offset and
   * makes `formatRelative` announce an analysis written "in 5 hours".
   *
   * There is no AUTHOR on an entry. The engine writes these and
   * `_append_history` carries no actor, so the timeline says what changed and
   * when, and cannot say who. Recorded in BACKEND-GAPS.md.
   */
  history: (row.history ?? []).map((entry) => ({
    title: entry.title ?? '',
    description: entry.description ?? '',
    changedAt: toInstant(entry.changed_at),
  })),

  /**
   * Sensor tags pinned to this anomaly.
   *
   * `id` here is the SENSOR TAG's id and not the join row's — `_resolve_sensor_tags`
   * builds the out-shape from the tag. Which is convenient: it is exactly what
   * `PUT /anomalies/{id}/sensor-tags` wants back as `sensor_tag_id`.
   *
   * `type` is `pinned` or `unpinned`. Unpinning flips the column in place rather
   * than deleting the row, so an unpinned tag still arrives here and a consumer
   * that ignores `type` would draw every tag anyone ever considered.
   */
  sensorTags: (row.sensor_tags ?? []).map((tag) => ({
    sensorTagId: tag.id,
    type: tag.type,
    name: tag.name ?? '',
    valueType: tag.value_type,
    /**
     * The tag's CONNECTION's state, not the tag's — `SensorTagSummary` says so
     * on the schema. A paused connection keeps its pins visible and flagged,
     * which is the only way somebody reading a flat trace can tell "this sensor
     * reported nothing" apart from "nobody is collecting this sensor".
     *
     * `=== false` rather than a truthy test: an absent field must not read as a
     * confident "paused" and put a warning on every card, which is the failure
     * `hasAnalysis` documents in the other direction.
     */
    connectionPaused: tag.is_active === false,
    /**
     * The ENGINE attached this tag — it is one of the sensors that raised the
     * alert (`AnomalySensorTagOut.is_source`). The server refuses to remove one
     * ("Source tags can't be removed."), so the screen offers no remove control
     * on it — unpinning is allowed and offered. `=== true`: a payload from before
     * the field shipped must read as "a person added this", not as a source.
     */
    isSource: tag.is_source === true,
  })),

  /* Past anomalies the system considers similar — full list items, so each one
     renders with the same card the list uses and links to its own screen. The
     relationship is directed and system-derived; nothing a person does creates
     one. */
  similarAnomalies: (row.similar_anomalies ?? []).map(toAnomaly),

  /* Documents cited for this anomaly, pinned to a REVISION rather than to a
     document: `revision` is the numbered version that was actually read, which
     is the only honest way to cite a file that changes. */
  documentReferences: (row.document_references ?? []).map((reference) => ({
    documentId: reference.id,
    revision: reference.revision,
    name: reference.name,
    documentType: reference.document_type,
  })),

  /* Work orders raised against this anomaly — `WorkOrderListItem`s, the same
     shape the board reads, mapped by the module that owns it rather than
     re-derived here. */
  workOrders: (row.work_orders ?? []).map(workOrderFromWire),
})

/**
 * The private name the rest of this file uses. Exported above under a wire-facing
 * name because the DETAIL STREAM pushes exactly this shape and has to map it —
 * the SSE event is the same `AnomalyOut` the GET returns, so it must go through
 * the same mapper rather than a second one that could drift from it.
 */
const toAnomalyDetail = anomalyDetailFromWire

/**
 * The facet counts, read as DATA rather than against a known list of keys.
 *
 * `Object.entries` over what the server sent, in the order it sent it. That is
 * the whole point: `AnomalyStatus` has five members today and the design this
 * screen is built from wants a sixth (`snoozed`), so a hardcoded tile list would
 * be wrong the day it lands — silently, by omitting a tile whose count was
 * never zero. Iterating the response means the new facet appears on its own.
 *
 * Insertion order survives `JSON.parse`, and the server builds both dicts by
 * walking its enum, so this order IS the backend's declared order. Nothing here
 * re-sorts it — a severity ranking invented client-side would be exactly the
 * kind of hardcoding this module exists to avoid.
 *
 * @returns {Array<{key: string, count: number}>}
 */
const toCounts = (group) =>
  Object.entries(group ?? {}).map(([key, count]) => ({ key, count: Number(count) || 0 }))

/**
 * One page of anomalies, with the facet counts that came with it.
 *
 * Every parameter is omitted when unset rather than sent as null: FastAPI's
 * `Query(default=None)` treats an absent key as "no filter", and an explicit
 * empty string would be parsed against the enum and rejected with a 422.
 *
 * @param {{
 *   status?: string, severity?: string, hasAnalysis?: boolean, search?: string,
 *   plantId?: string, lineId?: string, machineId?: string,
 *   place?: ?{id: string, tier: string},
 *   createdFrom?: string, createdTo?: string,
 *   sortBy?: string, sortDir?: string, page?: number, pageSize?: number,
 * }} filters
 */
export async function fetchAnomalies(filters, signal) {
  const data = await get(endpoints.anomalies.list, {
    signal,
    params: toParams(filters),
  })

  return {
    /* `anomalies`, not `items`. Every collection in this API names itself —
       `users`, `sensors`, `work_orders` — and reading the wrong key here is
       silent: the list renders empty with `total: 0` and no error, which looks
       like an account with no anomalies rather than a client fault. */
    items: (data?.anomalies ?? []).map(toAnomaly),
    // Echoed back rather than assumed from what was asked for, so a clamped or
    // defaulted page moves the footer instead of being contradicted by it.
    page: data?.page ?? filters.page,
    pageSize: data?.page_size ?? filters.pageSize,
    total: data?.total ?? 0,
    totalPages: data?.total_pages ?? 0,
    statusCounts: toCounts(data?.counts?.status),
    severityCounts: toCounts(data?.counts?.severity),
  }
}

/**
 * App filters → the query string the endpoint declares.
 *
 * snake_case on the wire, and empty values dropped entirely. Written out one key
 * at a time rather than looped over a mapping, because a silent typo in a
 * camelCase key would be ignored by FastAPI: the server would answer the
 * unfiltered page 1 and the screen would show the wrong rows under the right
 * chips. Same hazard `features/sensors/api.js` documents for `page_size`.
 */
function toParams({
  status,
  severity,
  hasAnalysis,
  search,
  plantId,
  lineId,
  machineId,
  place,
  createdFrom,
  createdTo,
  sortBy,
  sortDir,
  page,
  pageSize,
}) {
  const params = {
    sort_by: sortBy || DEFAULT_SORT_FIELD,
    sort_dir: sortDir || DEFAULT_SORT_DIRECTION,
    page: page || 1,
    page_size: pageSize || DEFAULT_PAGE_SIZE,
  }
  if (status) params.status = status
  if (severity) params.severity = severity
  /**
   * `has_analysis`, a boolean, where this sent `rca_status` and a five-value
   * string.
   *
   * `!= null` and not a truthiness test: `false` is a real and useful filter —
   * "show me everything nobody has investigated" is the whole reason the app
   * shell's severity band exists — and `if (hasAnalysis)` would drop it
   * silently, which is exactly how `rca_status` failed. An unrecognised query
   * param is IGNORED by FastAPI rather than rejected, so the server answered the
   * unfiltered page 1 under the right heading and nothing looked wrong.
   */
  if (hasAnalysis != null) params.has_analysis = hasAnalysis
  /* Matches `serial_number` and `title`, and nothing else —
     `apply_search(query, search, Anomaly.serial_number, Anomaly.title)`. It runs
     AFTER the counts are taken (backend dafc451, 2026-10-02), so it narrows the
     rows and leaves the facet tiles alone. */
  if (search?.trim()) params.search = search.trim()
  if (plantId) params.plant_id = plantId
  if (lineId) params.line_id = lineId
  if (machineId) params.machine_id = machineId
  /**
   * One place and everything inside it — `apply_asset_scope`. Both keys or
   * neither: the server answers 400 when only one is sent, which is better than
   * the silent unfiltered page an unknown key would get.
   *
   * Applied BEFORE the counts, like the date range, so the tiles describe the
   * chosen place. That is the difference from `plant_id`/`line_id`/`machine_id`
   * above, which narrow the same rows but leave the tiles at fleet scope.
   */
  if (place?.id && ASSET_TYPE_OF_TIER[place.tier]) {
    params.asset_type = ASSET_TYPE_OF_TIER[place.tier]
    params.asset_id = place.id
  }
  if (createdFrom) params.created_from = createdFrom
  if (createdTo) params.created_to = createdTo
  return params
}

/** One anomaly. */
export async function fetchAnomaly(id, signal) {
  return toAnomalyDetail(await get(endpoints.anomalies.detail(id), { signal }))
}

/**
 * Save. `PUT`, and a FULL replace — this used to be the one PATCH in the API.
 *
 * `AnomalyUpdate` is `severity`, `status` and `status_reason`, all REQUIRED.
 * `status_reason` is nullable but has no default, so the key must be present on
 * every save — `null` for anything but a dismiss — or the request is a 400. So a
 * status change
 * has to carry the severity it is not changing, and a caller holding a stale copy
 * writes that stale severity back. That is why `useAnomaly` keeps `staleTime: 0`,
 * the same trap `updateSensor` and `saveWorkOrder` both document.
 *
 * ── `title` is not here, and it is not an omission ───────────────────────
 * It was on the old partial `AnomalyUpdate` and is on no schema now. An
 * anomaly's title is the detector's — written as "{sensor}: {what happened}" —
 * and the only thing that rewrites it is the copilot's `update_title` directive,
 * which carries a reasoning pair and appends to `history`. A field a person
 * retyped here would have no such trail. Offering an input whose value the
 * server drops is the failure `WorkOrderFormDialog` removed its estimate box
 * over.
 *
 * @param {{id: string, severity: string, status: string, statusReason?: string|null}} input
 */
export async function saveAnomaly({ id, severity, status, statusReason = null }) {
  return toAnomalyDetail(
    await put(endpoints.anomalies.detail(id), { severity, status, status_reason: statusReason }),
  )
}

/**
 * Where one anomaly lives in the app.
 *
 * Here rather than in the page, so the card, the list and any future deep link
 * all build it from one place — the same reason `pages/dataFusion/sections.js`
 * holds its path builders. A route written twice is a route that gets renamed
 * once.
 */
export const anomalyPath = (id) => `/app/anomalies/${id}`

/**
 * DEMO. Facets the endpoint does not report, as the counts row wants them.
 *
 * Returned in the same `{key, count}` shape `toCounts` produces from
 * `counts.status`, so the tile row renders both from one code path and neither
 * knows which of the two it is holding. That is the seam: when the API starts
 * counting these, this function reads them off the list response instead and the
 * screen does not change.
 *
 * `snoozed` is a STATUS the enum does not have yet; `unassigned` is not a status
 * at all — it is the absence of an owner on a record that has no owner field. They
 * travel together only because both are missing, which is why neither is in
 * `ANOMALY_STATUSES` and neither may be sent as a filter.
 */
export async function fetchAnomalyDemoFacets(signal) {
  const data = await get(endpoints.anomalies.demoFacets, { signal })
  return (data?.facets ?? []).map((row) => ({ key: row.key, count: Number(row.count) || 0 }))
}

/* ══ ROOT-CAUSE ANALYSIS ═══════════════════════════════════════════════ */

/**
 * The analysis, as the eight sections it actually has.
 *
 * ── Where the RCA went ───────────────────────────────────────────────────
 * There was an `AnomalyRca` row with its own endpoint, its own five-value status
 * enum, a `rca/request` POST and a partial PATCH. None of it exists. The
 * analysis is `Anomaly.content` — a JSONB column on the anomaly itself, arriving
 * on the ordinary detail read — with `history` beside it.
 *
 * `{}` is how "nobody has investigated this" is expressed, and it is the only
 * way: there is no queued, no draft, no approved, no failed. The four-state
 * lifecycle this module used to model is one boolean now.
 *
 * ── This client CANNOT write it ──────────────────────────────────────────
 * `content` is written by exactly one path: the copilot dispatcher's
 * `("anomalies", "update_content")` handler, fed from
 * `POST /copilot/internal/turns/{id}/chunk`, which is engine-authenticated with
 * a secret the browser does not have. `AnomalyUpdate` has no content field, and
 * the client-facing `CopilotRequestedAction` accepts exactly one value —
 * `request_rca`. So there is no editor, and an editor would have a Save button
 * with nowhere to save. Asking for a write route is the headline item in
 * BACKEND-GAPS.md.
 */

/**
 * The LIST sections of the report, in the order it reads them.
 *
 * `analysis_description` is not here: it is a prose string rather than a list of
 * findings, and it leads the report rather than being one section among these.
 *
 * The ORDER is an editorial decision — symptoms, then the causes, then what the
 * analysis could not see, then what it excluded. The KEYS are the engine's:
 * `rca_engine/content.py` produces them and a test pins the set, so a name
 * invented here would render an empty section forever.
 *
 * ── Two of the engine's keys are deliberately not in this list ───────────
 * `recommended_actions` is in `ACTION_SECTIONS` — it is what to DO, which is the
 * Action tab's subject, and it was the one section of the report nobody reading
 * the diagnosis was looking for.
 *
 * `supporting_evidences` is rendered NOWHERE, by decision. The engine still
 * emits it — nine rows on a typical record — and every one of them is already
 * quoted verbatim inside the `root_causes` description it supports. Drawn as a
 * section of its own it is the same evidence a second time, detached from the
 * cause it belongs to, which is strictly worse than where it already appears.
 * Listed here rather than dropped silently so the next reader knows the key
 * exists and that this is a choice.
 */
export const RCA_SECTIONS = [
  'symptoms',
  'root_causes',
  'contributing_factors',
  'data_gaps',
  'ruled_out',
]

/** What the Action tab draws out of the same `content` object. */
export const ACTION_SECTIONS = ['recommended_actions']

/**
 * `content` → the sections to draw, empty ones dropped.
 *
 * Takes the key list rather than closing over one, because the same object is
 * now read by two tabs — `RCA_SECTIONS` on Analysis, `ACTION_SECTIONS` on
 * Action — and one function reading a parameter cannot drift from a second
 * function reading a different constant.
 *
 * ── Read as DATA against a known order, not trusted as a shape ───────────
 * `content` is a bare JSONB column with no schema server-side. The engine
 * guarantees all eight keys today and a test holds it there, but the shape has
 * already changed once — the previous one was `what_happened` / `fault_domain` /
 * `ranked_causes[]` — and this screen went on rendering against keys the data no
 * longer had. So every access is defensive: a missing key is an empty section, a
 * non-array is an empty section, and a ninth key the engine adds tomorrow is
 * ignored rather than crashing the report.
 *
 * Empty sections are DROPPED rather than drawn with a dash. An analysis that
 * ruled nothing out has nothing to say under "Ruled out", and a column of empty
 * headings reads as a broken render.
 *
 * @returns {Array<{key: string, items: Array<{text: string, description: string}>}>}
 */
export function toRcaSections(content, keys = RCA_SECTIONS) {
  return keys.map((key) => ({
    key,
    items: (Array.isArray(content?.[key]) ? content[key] : [])
      .map((item) => ({
        /* Trimmed, although `_finding` already trims server-side. Cheap, and it
           is what makes the `.filter` below catch a whitespace-only text rather
           than rendering a blank row with a bullet beside it. */
        text: typeof item?.text === 'string' ? item.text.trim() : '',
        description: typeof item?.description === 'string' ? item.description.trim() : '',
      }))
      /* A finding with no text is not a finding. `description` is legitimately
         empty — `data_gaps` sets it to `""` on every row by construction. */
      .filter((item) => item.text),
  })).filter((section) => section.items.length > 0)
}

/**
 * The sensors the alert is about, from `content.symptoms`.
 *
 * One symptom row per sensor, "Name: what happened" — the engine writes the
 * name before the colon. Read only for the Timeseries empty state, to say WHICH
 * sensors the alert concerns while `sensor_tags` arrives empty for records
 * raised before `is_source` shipped. It is a label and never an id: nothing is
 * charted or linked from it.
 */
export function symptomSensorNames(content) {
  const rows = Array.isArray(content?.symptoms) ? content.symptoms : []
  const names = rows
    .map((row) => (typeof row?.text === 'string' ? row.text : ''))
    .map((text) => (text.includes(':') ? text.slice(0, text.indexOf(':')).trim() : ''))
    .filter(Boolean)
  return [...new Set(names)]
}

/**
 * The engine's account of its own run — `content.run` — or null.
 *
 * ── The Reasoning tab reads this, not `history` ──────────────────────────
 * `{started_at, finished_at, steps: [{step, title, found[]}], verdict}`. Five
 * steps in a fixed order (detected, context, candidates, weighed, concluded),
 * every sentence finished by the engine — the screen adds no wording.
 *
 * ── Validated, because older records hold an older shape ────────────────
 * Alerts analysed before 2026-10-12 have no `run`, or one shaped
 * `{model, consulted, candidates}`. Anything that is not a non-empty list of
 * `{title, found[]}` steps answers null, and the tab falls back to `history`
 * until somebody re-runs the analysis. A step with no title is dropped.
 *
 * Timestamps are naive UTC, normalised to instants here like every other stamp
 * on this record.
 */
export function toAnalysisRun(content) {
  const run = content?.run
  if (!run || !Array.isArray(run.steps)) return null
  const steps = run.steps
    .map((entry) => ({
      step: typeof entry?.step === 'string' ? entry.step : null,
      title: typeof entry?.title === 'string' ? entry.title.trim() : '',
      found: (Array.isArray(entry?.found) ? entry.found : [])
        .filter((line) => typeof line === 'string' && line.trim())
        .map((line) => line.trim()),
    }))
    /* A step with no sentences is KEPT: the engine drops empty lines, and the
       five are numbered by position, so losing one would renumber the rest. */
    .filter((entry) => entry.title)
  if (!steps.length) return null

  const startedAt = toInstant(run.started_at)
  const finishedAt = toInstant(run.finished_at)
  const took = startedAt && finishedAt ? (Date.parse(finishedAt) - Date.parse(startedAt)) / 1000 : NaN
  const verdict = run.verdict && typeof run.verdict === 'object' ? run.verdict : null

  return {
    steps,
    startedAt,
    finishedAt,
    /* Whole seconds, at least one: "took 0 seconds" reads as a run that never happened. */
    seconds: Number.isFinite(took) && took >= 0 ? Math.max(1, Math.round(took)) : null,
    verdict: verdict
      ? {
          /* `process` | `instrumentation` | `unclear`, and `high` | `medium` |
             `low` (`rca_engine/report.py`); the engine sends "" when it has
             none, which reads as null here and draws no badge. */
          faultDomain: typeof verdict.fault_domain === 'string' && verdict.fault_domain ? verdict.fault_domain : null,
          likelihood: typeof verdict.likelihood === 'string' && verdict.likelihood ? verdict.likelihood : null,
          matched: typeof verdict.matched === 'string' ? verdict.matched.trim() : '',
          tie: verdict.tie === true,
        }
      : null,
  }
}

/** The prose that leads the report. A string, not a list — see `RCA_SECTIONS`. */
export const rcaDescription = (content) =>
  typeof content?.analysis_description === 'string' ? content.analysis_description.trim() : ''

/**
 * Whether there is an analysis to render.
 *
 * Tested on the CONTENT wherever the content is in hand, not on `hasAnalysis`.
 * The two agree — `has_analysis` is `bool(anomaly.content)` server-side — but a
 * list row carries only the boolean and a detail row carries the object, and the
 * object is the one that decides whether the report has anything in it.
 */
export function hasRcaContent(anomaly) {
  return Boolean(anomaly?.content && Object.keys(anomaly.content).length > 0)
}

/* ══ SENSOR TAGS ═══════════════════════════════════════════════════════ */

/**
 * Replace the anomaly's sensor-tag set.
 *
 * ── The body is the WHOLE set, and that is the hazard here ──────────────
 * `replace_sensor_tags` delta-syncs against what it is sent: a tag omitted from
 * the body is soft-deleted. So this is `saveAnomaly`'s trap one level worse — a
 * caller that posts only the tag it is adding does not fail, it deletes every
 * other tag on the anomaly and returns 200. Callers must build `tags` from the
 * record they just read, which is why the mutation around this reads the cache
 * written by `useAnomaly`/`useAnomalyStream` rather than keeping its own list.
 *
 * ── `type` and membership are two different controls now ───────────────
 * The endpoint distinguishes them and so does the screen:
 *
 *   in the body as `pinned`     attached to the anomaly, and charted
 *   in the body as `unpinned`   attached to the anomaly, not charted
 *   absent from the body        not attached at all; the row is soft-deleted
 *
 * So Add writes an `unpinned` row, Pin and Unpin flip `type` in place, and
 * Remove drops the tag from the body. Three controls, three spellings, and none
 * of them does a second thing quietly.
 *
 * ── Adding is checked harder than flipping ─────────────────────────────
 * `verify_sensor_tags` validates only the ids NEW to the set, so pinning and
 * unpinning an attached tag never re-runs the location and grant checks. That is
 * deliberate server-side — "a tag already pinned stays removable even if the
 * grant later narrowed" — and it means the pin controls cannot fail for a reason
 * the reader would have to be told about.
 *
 * @param {{anomalyId: string, tags: Array<{sensorTagId: string, type: string}>}} input
 * @returns {Promise<Array<object>>} the resolved set, as `AnomalySensorTagOut`
 */
export async function saveAnomalySensorTags({ anomalyId, tags }) {
  const data = await put(
    endpoints.anomalies.sensorTags(anomalyId),
    /* A bare ARRAY, not an object. `Bounded[AnomalySensorTagIn]` is a list at
       the top level of the body — there is no envelope key to nest it under, and
       a `{tags: [...]}` would be a 422 against a list schema. */
    tags.map((tag) => ({ sensor_tag_id: tag.sensorTagId, type: tag.type })),
  )

  return Array.isArray(data) ? data : []
}

/**
 * The most sensors that may be CHARTED at once.
 *
 * A product rule, not a server one: `Bounded` caps a request body's list at 500
 * (`request_list_max_items`), and nothing server-side distinguishes a pinned tag
 * from an unpinned one by count. Twenty is the point past which a grid of live
 * traces stops being a board somebody reads and becomes a wall — and each pinned
 * card is a readings fetch of its own, so the ceiling is also what stops one
 * anomaly firing a hundred requests on open.
 *
 * Attaching is NOT capped by this. A sensor can be added to the anomaly and left
 * unpinned; that is the whole point of the two steps.
 */
export const MAX_PINNED_SENSORS = 20

/**
 * The anomaly's own asset, as the sensor list's filter for it.
 *
 * ── The EXACT level, not the subtree, and the server is why ─────────────
 * `GET /v1/sensors` offers both: `plant_id`/`line_id`/`machine_id` compare one
 * column each, while `asset_type`/`asset_id` match the asset or anything beneath
 * it. This returns the first kind, because `verify_sensor_tags` links a tag only
 * when its sensor sits at the anomaly's exact `(plant_id, line_id, machine_id)` —
 * so the exact filter is the only one whose results can all actually be added.
 *
 * The picker listed the subtree for a while, on the argument that a plant-level
 * anomaly is about sensors on its machines. It is, and that is a real gap — but
 * offering rows the next request refuses is not a way to express it. The list now
 * matches what the endpoint accepts, and the gap is stated in BACKEND-GAPS.md
 * where it can be fixed rather than worked around.
 *
 * What that costs, said plainly: an ingestion-outage anomaly is filed at the
 * plant and names nine sensors that are all on machines, so this returns the
 * plant's own sensors — usually none. The dialog says so rather than looking
 * broken.
 *
 * Deepest first, because that is where the anomaly is attached: `machine_id` is
 * set for a machine-level anomaly and the other two are null, and so on up. An
 * anomaly with no attachment at all returns null and the picker says it has
 * nothing to offer rather than listing the whole account.
 */
export function anomalyAssetScope(anomaly) {
  if (anomaly?.machineId) return { level: 'machine', filter: { machineId: anomaly.machineId } }
  if (anomaly?.lineId) return { level: 'line', filter: { lineId: anomaly.lineId } }
  if (anomaly?.plantId) return { level: 'plant', filter: { plantId: anomaly.plantId } }
  return null
}

/* ══ SENSOR TAG READINGS ═══════════════════════════════════════════════ */

/**
 * Whether a rejection is the readings endpoint saying `from` is not before `to`.
 *
 * Matched on the FIELD and the status, not on the sentence: `fields.from` is the
 * structured half of `ValidationAppError` and is the part a backend may not
 * reword. The endpoint's two other `from`/`to` errors — before the window, after
 * the window — are unreachable from this client, which only ever sends a `from`
 * the server itself returned and never sends a `to` at all.
 */
const isPastWindowEnd = (error) =>
  error?.status === 400 && typeof error?.fields?.from === 'string'

/**
 * A reading stamp as whole MICROSECONDS, which is the resolution the server works in.
 *
 * `Date.parse` is milliseconds and would collapse the one-microsecond difference
 * this file's paging turns on. `reading.stamp` writes
 * `isoformat(sep=" ", timespec="microseconds")` — a space separator and exactly
 * six fractional digits — so the fraction is read off the string and added back
 * as an integer.
 *
 * Returns NaN for anything unparseable, which every caller treats as "cannot
 * tell", never as zero.
 */
export function readingMicros(value) {
  if (typeof value !== 'string' || !value) return NaN
  const [base, fraction = ''] = value.replace(' ', 'T').split('.')
  const ms = Date.parse(`${base}Z`)
  if (!Number.isFinite(ms)) return NaN
  return ms * 1000 + Number(`${fraction}000000`.slice(0, 6))
}

/**
 * Whether a page was FULL, and therefore whether another may follow.
 *
 * ── Read off the server's own narrowing, not off a page-size constant ───
 * `list_anomaly_sensor_tag_readings` ends with:
 *
 *     if len(readings) == settings.sensor_reading_history_page_size:
 *         end = min(end, readings[-1]["timestamp"] + one_us)
 *     return success({..., "to": reading.stamp(end)})
 *
 * So a FULL page answers with `to` exactly one microsecond past its last
 * reading, and a SHORT page answers with the end it was asked for — which is the
 * window end. Comparing `to` against the last row's stamp therefore detects the
 * narrowing directly, and the page size never enters into it.
 *
 * ── This replaces a hardcoded 50, and the hardcoding was a live bug ─────
 * `READING_PAGE_SIZE = 50` mirrored `sensor_reading_history_page_size`, written
 * out the way `DEFAULT_PAGE_SIZE` and `MAX_PAGE_SIZE` mirror their `Query(...)`
 * bounds. That setting is 100. A full page of 100 failed `length === 50`, every
 * trace stopped after one page believing the window exhausted, and the charts
 * reported "Full window" over a partial one — so a detection band falling in the
 * unfetched remainder simply did not appear, on three cards out of four.
 *
 * A mirrored constant is safe for a bound the server REJECTS deviation from, and
 * unsafe for one it merely uses: nothing fails when the second kind drifts, so
 * nothing announces it. This one is now derived from the response.
 */
function pageWasFull(rows, to) {
  if (!rows.length) return false
  const last = readingMicros(rows[rows.length - 1]?.timestamp)
  const edge = readingMicros(to)
  if (!Number.isFinite(last) || !Number.isFinite(edge)) return false
  return edge === last + 1
}

/**
 * A reading's timestamp → an instant the browser can parse.
 *
 * NOT `toInstant`. `reading.stamp` writes `isoformat(sep=" ")` — "2026-10-07
 * 06:48:19.619000", with a SPACE where every other timestamp in this API has a
 * `T`. Appending a `Z` to that, which is all `toInstant` does, produces a string
 * whose parse is implementation-defined: it happens to work in V8 and is not
 * required to work anywhere, which is the worst of both. So the separator is
 * normalised first and then the designator restored.
 */
export const readingInstant = (value) =>
  typeof value === 'string' && value !== '' ? toInstant(value.replace(' ', 'T')) : null

/**
 * ONE page of a pinned tag's readings, mapped and with its cursor.
 *
 * ── It used to fetch the whole window in a loop, and that was the problem ──
 * Eight requests fired the moment a card mounted, four cards to a board — up to
 * thirty-two requests to draw a screen, most of them for readings nobody had
 * scrolled to yet. The loop is gone: this returns one page and says whether
 * there is another, and `useSensorTagReadings` asks for the next one when the
 * reader actually pans to the end of what is loaded.
 *
 * ── "Is there more" is read off the server's own narrowing ─────────────
 * See `pageWasFull`. A full page's `to` lands one microsecond past its last
 * reading; a short page's `to` is the end it was asked for. Nothing here depends
 * on knowing `sensor_reading_history_page_size`, which is the mistake the
 * previous version made and which silently truncated every trace when that
 * setting moved from 50 to 100.
 *
 * @param {{anomalyId: string, sensorTagId: string, from?: string}} input
 * @returns {Promise<{readings: Array<{at: string|null, value: number}>, next: string|null}>}
 */
export async function fetchSensorTagReadingPage({ anomalyId, sensorTagId, from }, signal) {
  const params = {}
  /* Omitted when unset, which asks for the start of the anomaly window — the
     server's own default and the only place this screen ever starts. Sending a
     `from` outside `[window_start, window_end]` is a 400 rather than a clamp. */
  if (from) params.from = from

  let data
  try {
    data = await get(endpoints.anomalies.sensorTagReadings(anomalyId, sensorTagId), {
      signal,
      params,
    })
  } catch (error) {
    /**
     * ── The one refusal that means "the window ends here" ──────────────
     * Reachable from a correct cursor in exactly one case: a full final page
     * whose last reading is stamped at `window_end`. The handler then narrows
     * `to` to `window_end + 1µs`, which is also the default `to`, so this page's
     * cursor asks for an empty range and the server answers
     * `400 {"from": "Must be before \`to\`."}`. No response distinguishes that
     * page from a full one with more behind it.
     *
     * Treated as the end of the window, and ONLY when a cursor was sent. The
     * same response on the FIRST request means the anomaly's own window is
     * degenerate, which is a real fault and is rethrown. A catch any wider is
     * the `fetchAnomalyRca` 404 swallow again — that one turned a deleted route
     * into a confident "not investigated" across the estate, because it caught a
     * condition that later stopped meaning what it used to.
     */
    if (from && isPastWindowEnd(error)) return { readings: [], next: null }
    throw error
  }

  const rows = Array.isArray(data?.readings) ? data.readings : []
  const readings = []
  for (const row of rows) {
    const value = Number(row?.value)
    // A non-numeric value is not plottable and is not drawn as a zero.
    if (Number.isFinite(value)) readings.push({ at: readingInstant(row?.timestamp), value })
  }

  const next = pageWasFull(rows, data?.to) ? (data?.to ?? null) : null

  return {
    readings,
    /* Never hand back a cursor equal to the one just used. Not reachable through
       the handler as written; it is here so that a change to the narrowing rule
       degrades into one wasted request rather than a loop that re-reads the same
       page forever. */
    next: next && next !== from ? next : null,
  }
}

/*
 * ── The RETIRED RCA client ───────────────────────────────────────────────
 * Every function below called a route that no longer exists. Kept commented
 * rather than deleted, per the standing recipe: they are the record of a
 * contract this app was built against, and the four-state lifecycle they model
 * is what the screens still carry copy for.
 *
 * `fetchAnomalyRca` is the one worth reading twice. It swallowed a 404 and
 * answered `null`, because a 404 genuinely did mean "nobody has asked yet" — and
 * when the route was removed entirely, that swallow turned a dead endpoint into
 * a confident "not investigated" on every anomaly in the estate. No error, no
 * empty state, nothing for anybody to notice. A catch that narrow is only safe
 * while the thing it catches stays narrow.
 *
//
// /**
//  * `AnomalyRcaStatus`, in the order the backend declares it — which is also the
//  * lifecycle order.
//  *
//  *   pending   — never asked for (the request endpoint creates rows as `queued`,
//  *                so a `pending` ROW is seed or fixture data, and `pending` is also
//  *                what the list endpoint reports when no row exists at all)
//  *   queued    — requested; `content` is still `{}` and nothing has generated it
//  *   draft     — content exists, not yet approved
//  *   approved  — content exists and has been signed off
//  *   failed    — the request or the run itself failed; `last_error` says why
//  *
//  * Only `draft` and `approved` may be set through `PATCH .../rca`; a validator
//  * rejects the others, because `queued` and `failed` are owned by the request
//  * endpoint and the engine respectively.
//  *
//  * `failed` was missing from this list while rows were already landing in it — the
//  * enum gained it with the RCA seam hardening. Anything that RENDERS a status has to
//  * handle it, or a failed analysis shows as an unlabelled hole rather than as the
//  * one state a user most needs to see.
//  *
//  * ── Filtering on these ────────────────────────────────────────────────────
//  * `GET /anomalies?rca_status=` accepts every member. `pending` is the one worth
//  * reading twice: the server derives the list item's `rca_status` as `rca.status if
//  * rca else pending`, and the filter matches a missing row to suit — so asking for
//  * `pending` asks for every anomaly nobody has investigated.
//  */
// export const RCA_STATUSES = ['pending', 'queued', 'draft', 'approved', 'failed']
// export const RCA_SETTABLE_STATUSES = ['draft', 'approved']
//
// const toRca = (row) => ({
//   id: row.id,
//   anomalyId: row.anomaly_id,
//   // An opaque JSONB blob. No schema exists for it server-side, so nothing here
//   // may assume a key — see `hasRcaContent`.
//   content: row.content ?? {},
//   status: row.status,
//   createdAt: toInstant(row.created_at),
//   updatedAt: toInstant(row.updated_at),
// })
//
// /**
//  * One anomaly's RCA, or `null` when none has been requested.
//  *
//  * ── A 404 here is DATA, not a failure ─────────────────────────────────────
//  * `AnomalyRca` rows are created by `POST .../rca/request` and by nothing else,
//  * so an anomaly nobody has investigated has no row and this endpoint answers
//  * `NotFoundError("RCA")` at 404. That is the overwhelmingly common case — every
//  * anomaly starts there.
//  *
//  * Swallowed here rather than at the call site so there is exactly one place that
//  * knows it. Letting it reach the query would put an error panel on the detail
//  * screen of every new anomaly, and letting each screen special-case the status
//  * code would mean the next screen forgets to.
//  *
//  * Only 404 is swallowed. A 500, a timeout or a 401 still throw, because those
//  * genuinely are failures and the screen must say so.
//  */
// export async function fetchAnomalyRca(anomalyId, signal) {
//   try {
//     return toRca(await get(endpoints.anomalies.rca(anomalyId), { signal }))
//   } catch (error) {
//     if (error?.status === 404) return null
//     throw error
//   }
// }
//
// /**
//  * Ask for an analysis. 202, and the row comes back in `queued`.
//  *
//  * Idempotent server-side — a second call returns the existing row with a
//  * different message rather than failing — so the button does not need to guard
//  * against a double press to stay correct.
//  *
//  * The body names who is asking. The server requires it and defaults nothing, so an
//  * analysis requested from a screen and one requested through the copilot stay
//  * distinguishable afterwards — a default would have quietly made every row look
//  * like a screen.
//  *
//  * Worth knowing when reading the UI this drives: **nothing generates the content
//  * yet.** The endpoint carries a standing note that the real trigger, and whatever
//  * flips `queued` to `draft`, is still to be wired. So the honest post-request
//  * state is "queued", and the screen says that rather than implying an analysis is
//  * on its way in a minute.
//  */
// export async function requestAnomalyRca(anomalyId, requestedVia = 'ui') {
//   return toRca(await post(endpoints.anomalies.rcaRequest(anomalyId), { requested_via: requestedVia }))
// }
//
// /**
//  * What this app renders as structured RCA fields — `what_happened`,
//  * `fault_domain`, `ranked_causes`, `ruled_out`, `data_gaps`.
//  *
//  * There is no list of them here any more, and that is the point of this note.
//  *
//  * ── There was one, and it drove an "Additional fields" card ────────────────
//  * `content` is a bare JSONB column with no server-side schema, so anything the
//  * model emits beyond those five keys was unknown to this client. It used to be
//  * collected by `unknownRcaFields()` and dumped as JSON at the foot of the
//  * analysis, on the reasoning that a value saved but invisible cannot be told
//  * apart from one that was lost.
//  *
//  * That was the wrong trade. The generator is still being built and nothing
//  * constrains the column, so the card's size is set by however much unmapped data
//  * the API happens to return — which turns an occasional footnote into a wall of
//  * JSON under a human-readable analysis. A reviewer reading a root cause is not
//  * the person who needs to see the raw payload.
//  *
//  * ── Nothing is lost by removing it ─────────────────────────────────────────
//  * That was the real worry, and it was already handled somewhere better.
//  * `PATCH .../rca` replaces `content` **wholesale** — there is no merge
//  * server-side — so an editor sending only the fields it renders would delete
//  * every other key permanently on the first save. The editor spreads
//  * `...rca.content` before its own five keys, which preserves the remainder
//  * whether or not anything displays it. The card was never what kept the data;
//  * the spread is. See `save()` in `AnomalyDetailPage`.
//  *
//  * The three keys the generator retired — `hypothesis`, a flat
//  * `evidence_citations` array and a `confidence` float — are carried through the
//  * same way. They are no longer shown, which is now true of every unmapped key
//  * rather than a special case.
//  */
//
// /**
//  * Likelihood values a ranked cause is expected to carry.
//  *
//  * Not an enum the server declares — nothing constrains this field — so an
//  * unrecognised value is preserved and offered rather than coerced. See
//  * `LikelihoodField`.
//  */
// export const LIKELIHOODS = ['high', 'medium', 'low']
//
// /**
//  * Update the analysis: its content, its status, or both.
//  *
//  * ── Genuinely partial, unlike the rest of this API ────────────────────────
//  * `AnomalyRcaUpdate` declares `content: dict | None` and `status: ... | None`, so
//  * an omitted key means "leave it". Approving therefore sends `{status}` alone and
//  * cannot disturb a draft's text, and saving an edit sends `{content}` alone and
//  * cannot flip the status out from under the reviewer.
//  *
//  * ── `status` is validated to two values ──────────────────────────────────
//  * A field validator rejects anything but `draft` and `approved`: `pending` and
//  * `queued` belong to the request endpoint and cannot be set here. Sending one
//  * would come back 422 naming the field.
//  *
//  * @param {{anomalyId: string, content?: object, status?: 'draft'|'approved'}} input
//  */
// export async function updateAnomalyRca({ anomalyId, content, status }) {
//   const body = {}
//   if (content !== undefined) body.content = content
//   if (status !== undefined) body.status = status
//
//   return toRca(await patch(endpoints.anomalies.rca(anomalyId), body))
// }
//
// /**
//  * Whether an RCA row actually carries an analysis.
//  *
//  * `status` alone cannot answer this: a `queued` row exists with `content: {}`,
//  * and `content` is a schemaless JSONB column, so the only honest test is whether
//  * there are any keys in it. Checked rather than assumed because the difference
//  * decides between rendering an analysis and rendering "queued" — and an empty
//  * object rendered as an analysis is a blank panel with a heading over it.
//  */
// export function hasRcaContent(rca) {
//   return Boolean(rca?.content && Object.keys(rca.content).length > 0)
// }
//
// /**
//  * Whether an analysis has been asked for at all.
//  *
//  * Two shapes mean the same thing and both have to be handled: no row (the endpoint
//  * 404s, `fetchAnomalyRca` returns `null`) and a row sitting at `pending`. The
//  * request endpoint only ever creates rows as `queued`, so a `pending` row is one
//  * nobody has started — seeded or fixture data — and it is NOT work in progress.
//  */
// export function awaitsRcaRequest(rca) {
//   return !rca || rca.status === 'pending'
// }
//
// /**
//  * Requested, and still running. The one state the radar belongs to.
//  *
//  * `queued` is the whole test, plus the content guard for the case where generation
//  * has landed but nothing has moved the status off `queued` yet — content on screen
//  * beats a spinner over it. `pending` deliberately does not qualify: a screen that
//  * shows a sweeping radar over a row nobody has requested is claiming the server is
//  * working when it has not been asked to.
//  */
// export function isRcaRunning(rca) {
//   return rca?.status === 'queued' && !hasRcaContent(rca)
// }

/**
 * The hierarchy, as the segments to draw.
 *
 * Nulls dropped, order preserved: an anomaly raised at the plant yields one
 * segment, one raised at a machine yields three. The deepest present segment is
 * the level it attached to, which is why the caller renders the last one at full
 * strength and the rest dim — the same treatment the sensors table gives a
 * parent trail.
 *
 * Returns an empty array rather than a placeholder when every level is null. The
 * screen states that as an absence; it must not invent "Unknown".
 *
 * @returns {string[]}
 */
export function hierarchyTrail(hierarchy) {
  return [hierarchy?.plant, hierarchy?.line, hierarchy?.machine].filter(Boolean)
}

/**
 * The name of the place an anomaly is attached at — the deepest one present.
 *
 * Alerts are raised at a line or a whole site too (ANM-305 has no machine,
 * ANM-306 neither a machine nor a line), so "the machine, else the line" named
 * nothing for a site-level alert. Null only when every level is.
 */
export const anomalyPlaceName = (hierarchy) => hierarchyTrail(hierarchy).at(-1) ?? null
